/**
 * 認証ガードコンポーネント
 */

import { AlertCircle, Loader2, LogIn, Shield } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { apiClient } from "@/lib/api-client";
import { AccessDeniedScreen } from "./access-denied-screen";
import { AdminSetupScreen } from "./admin-setup-screen";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, error, login, user, refresh } = useAuth();
  const [adminExists, setAdminExists] = useState<boolean | null>(null);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  // 管理者の存在を確認
  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await apiClient.api.admin.status.$get();
      if (response.ok) {
        const data = await response.json();
        setAdminExists(data.adminExists);
      }
    } catch {
      console.error("Failed to check admin status");
    } finally {
      setCheckingAdmin(false);
    }
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  // ローディング中
  if (isLoading || checkingAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // エラー
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
            <CardTitle className="mt-4">エラーが発生しました</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={login}>再度ログイン</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 未認証
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="mx-auto h-12 w-12 text-primary" />
            <CardTitle className="mt-4">Remote BINGO Admin</CardTitle>
            <CardDescription>管理者としてログインしてください</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button onClick={login}>
              <LogIn className="mr-2 h-4 w-4" />
              Googleでログイン
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 認証済み: 管理者チェック

  // 管理者が存在しない場合 → セットアップ画面
  if (!adminExists) {
    return (
      <AdminSetupScreen
        onComplete={() => {
          setAdminExists(true);
          refresh(); // ユーザー情報を再取得してisAdminを反映
        }}
      />
    );
  }

  // 管理者が存在するが、このユーザーは管理者ではない → アクセス拒否
  if (!user?.isAdmin) {
    return <AccessDeniedScreen />;
  }

  return <>{children}</>;
}
